/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file index.js
 * @description User Services
 */

// Core
import endpoints from '@nephro/config/endpoints';
import { externalConnector } from '@nephro/core/connector';

// Endpoints
const { user: userEndpoints } = endpoints;

export class UserService {
  constructor(http) {
    this.http = http;
  }

  /**
   * Get user info after login
   * @param { string } username - identification or username
   * @param { bool } isPatient - Is patient or staff flag.
   * @param { string } documentType - Patient identification type
   * @return { Object } - Get users list.
   */
  getUserLoginInfo = async (username, isPatient, documentType) => {
    const queryParam = `?isPatient=${isPatient}&userName=${username}&documentType=${documentType}`;
    let url = userEndpoints.userLogin;
    url = `${url}${queryParam}`;
    return this.http.post(url);
  };

  /**
   * Get user info after login fpr register steps
   * @param { string } username - identification or username
   * @param { bool } isPatient - Is patient or staff flag.
   * @param { string } documentType - Patient identification type
   * @return { Object } - Get users list.
   */
  getUserDataRegister = async (username, isPatient, documentType) => {
    const queryParam = `?isPatient=${isPatient}&userName=${username}&documentType=${documentType}`;
    let url = userEndpoints.userDataRegister;
    url = `${url}${queryParam}`;
    return this.http.post(url);
  };

  /**
   * Get user info after login
   * @param { Object } params - { moduleName, permission, userName }
   * @return { Object } - Get users list.
   */
  getValidatePermission = async (params) => {
    const queryParam = new URLSearchParams(params).toString();
    let url = userEndpoints.validatePermission;
    url = `${url}?${queryParam}`;
    return this.http.get(url);
  };

  /**
   * Get users list with pagination
   * @param { string } paged - Activate pagination.
   * @param { string } limit - Items per page.
   * @param { string } page - Current page.
   * @return { Object } - Get users list.
   */
  getUsers = async (paged, limit, page) => {
    const queryParam = `?limit=${limit}&page=${page}&paged=${paged}`;
    let url = userEndpoints.base;
    if (paged) {
      url = `${url}${queryParam}`;
    }
    return this.http.get(url);
  };

  /**
   * Get user info by id
   * @param { string } id - User id.
   * @return { Object } - Get users list.
   */
  getUserById = async (id) => {
    const url = `${userEndpoints.base}/${id}`;
    return this.http.get(url);
  };

  /**
   * Get user info by username
   * @param { string } username - Username.
   * @return { Object } - Get user info.
   */
  getUserByUserName = async (username) => {
    const url = `${userEndpoints.base}/getUserByUserName/${username}`;
    return this.http.get(url);
  };

  /**
   * Get user info by username
   * @param { string } email - Email.
   * @return { Object } - Get user info.
   */
  getUserByEmail = async (email) => {
    const url = `${userEndpoints.base}/getUserByEmail/${email}`;
    return this.http.get(url);
  };

  /**
   * Delete user by id
   * @param { string } id - User id.
   * @return { Object } - Get users list.
   */
  deleteUserById = async (id) => {
    const url = `${userEndpoints.base}/${id}`;
    return this.http.delete(url);
  };

  /**
   * Update user
   * @param { string } name - User name.
   * @param { string } description - User description.
   * @param { string } id - User id.
   * @return { promise } - The user promise object.
   */
  updateUser = async (name, description, id) => {
    const body = { name, description };
    const url = `${userEndpoints.base}/${id}`;
    return this.http.update(url, body, {}, 'put');
  };
}

export default new UserService(externalConnector);
